import React from "react";
import { FaCircleInfo } from "react-icons/fa6";
import './GradientLegend.css'

export function showInfoTooltip(message: string, extraClassName?: string) {
    const [pos, setPos] = React.useState<{ top: number, left: number }>({ top: 0, left: 0 });
    const tooltipRef = React.useRef<HTMLDivElement>(null);

    const handleMouseEnter = (e: React.MouseEvent) => {
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        setPos({ top: rect.top - (tooltipRef.current?.offsetHeight || 0), left: rect.left + rect.width / 2 });
    };

    return (
        <div className="map-tooltip-wrapper" onMouseEnter={handleMouseEnter}>
            <FaCircleInfo style={{color:'#28aee1'}} />
            <div
                ref={tooltipRef}
                className={`map-custom-tooltip ${extraClassName ?? ""}`}
                style={{ top: pos.top, left: pos.left, transform: 'translateX(-50%)' }}
            >
                {message}
            </div>
        </div>
    );
}

function formatDateRange(): string {
    const tz = "America/New_York";

    const now = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(now.getDate() - 8);

    const yesterday = new Date();
    yesterday.setDate(now.getDate() - 1); // Subtract 1 day for yesterday

    const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone: tz,
        month: "2-digit",
        day: "2-digit",
        year: "numeric"
    });

    const startDate = formatter.format(sevenDaysAgo);
    const endDate = formatter.format(yesterday);

    return `${startDate} to ${endDate}`;
}


export function interpolateColor(
    value: number,
    min: number,
    max: number,
    includeRanges = false // Optional parameter to control if the ranges should be included
): { color: string, rangeCloserToMin?: string, rangeCloserToMax?: string } { // Optional properties
    // Calculate log values for min, max, and the given value
    const logMin = Math.log(min + 1);
    const logMax = Math.log(max + 1);
    const logValue = Math.log(value + 1);

    // Calculate the normalized ratio based on logarithmic scale
    const ratio = (logValue - logMin) / (logMax - logMin);

    // Interpolate the color based on the ratio
    let r = 0, g = 0, b = 0;

    if (ratio <= 0.2) {
        // Blue → Green
        b = 255;
        g = Math.round(255 * (ratio / 0.15));
    } else if (ratio <= 0.7) {
        // Green → Yellow
        g = 255;
        r = Math.round(255 * ((ratio - 0.15) / 0.5));
    } else if (ratio <= 0.9) {
        // Yellow → Red
        r = 255;
        g = Math.round(255 * (1 - (ratio - 0.65) / 0.2));
    } else {
        // Solid Red
        r = 255;
        g = 0;
        b = 0;
    }


    // Initialize the return object
    const result: { color: string, rangeCloserToMin?: string, rangeCloserToMax?: string } = {
        color: `rgb(${r},${g},${b})` // Always return color
    };

    // Only calculate ranges if includeRanges is true
    if (includeRanges) {
        // Midpoint where color shifts from green → yellow
        const logRangeCloserToMinEnd = (logMin + logMax) / 1.7;

        // Upper range where color shifts from yellow → red
        const logRangeCloserToMaxEnd = logMax - (logMax - logMin) * 0.1;

        // Convert back to normal scale
        const rangeCloserToMin = Math.exp(logRangeCloserToMinEnd) - 1;
        const rangeCloserToMax = Math.exp(logRangeCloserToMaxEnd) - 1;

        result.rangeCloserToMin = `${Math.round(rangeCloserToMin)}`;
        result.rangeCloserToMax = `${Math.round(rangeCloserToMax)}`;
    }

    return result;
}

interface LegendProps {
    min: number;
    max: number;
    title?: string;  // optional title
    tooltip?: string; // optional tooltip
}

const GradientLegend: React.FC<LegendProps> = ({ min, max, title, tooltip }) => {
    const gradientSteps = 10;

    // Distribute values logarithmically between min and max
    const gradientColors = Array.from({ length: gradientSteps }, (_, i) => {
        // Calculate the logarithmic value for the current step
        const logMin = Math.log(min + 1);
        const logMax = Math.log(max + 1);
        const logStep = logMin + ((logMax - logMin) * i) / (gradientSteps - 1);

        // Convert the log value back to the original scale
        const value = Math.exp(logStep) - 1; // Exp to reverse the log transformation

        // Get the interpolated color and ranges for display
        return interpolateColor(value, min, max, true);
    });

    // Get the last item from the array to access the color and ranges
    const { rangeCloserToMin, rangeCloserToMax } = gradientColors[gradientColors.length - 1];

    // ✅ Defaults
    const effectiveTitle = title ?? "Weekly Strikes";
    const effectiveTooltip =
        tooltip ??
        `This map is showing Cloud-to-Ground strikes from the following date periods: ${formatDateRange()}`;

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                fontSize: "0.9rem",
                fontWeight: "600",
                color: "white",
            }}
        >


            <div style={{ display: 'flex', gap: '0.3rem', marginBottom: "5px", fontSize: "0.75rem", alignItems:'center' }}>
                <span style={{ whiteSpace: 'nowrap' }}>{effectiveTitle}</span>
                {showInfoTooltip(effectiveTooltip)}
            </div>
            <div

                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.8rem"
                }}
            >
                <div
                    style={{
                        width: "20px",
                        height: "10rem",
                        background: `linear-gradient(to top, ${gradientColors.map(g => g.color).join(", ")})`,
                        border: "1px solid #ccc",
                    }}
                />
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        height: "9.5rem",
                    }}
                >
                    <span>{max}</span>
                    <span>{rangeCloserToMax}</span>
                    <span>{rangeCloserToMin}</span>
                    <span>{min}</span>
                </div>
            </div>
        </div>
    );
};

export default GradientLegend;
